// Permanent-storage upload for pasted inline images inside a template body.
//
// Why this exists: the rich editor (`RichBodyEditor`) inserts pasted
// images as `<img src="blob:..." data-pa-attach-id="…">` markers. The
// blob URL is a session-only browser handle — gone on page reload, gone
// for every other tab and user. The reply composer's send path already
// uploads these blobs to Supabase Storage and rewrites the `src` to the
// permanent public URL (see `ReplyComposer.tsx` ~line 469). Templates
// need the SAME treatment at save-time so a template authored today
// keeps its screenshot visible forever, in every browser, for every
// teammate. Without this step, the saved template body holds a
// `blob:` URL that fails the moment the page reloads.
//
// Where it's called:
//   - `SaveAsTemplateDialog.tsx`         — composer's "Save as template" path
//   - `routes/templates.tsx` editor save — Templates page edit-in-place
// Both pass the editor's current HTML; both write the returned (rewritten)
// HTML to `reply_templates.body`.

import { supabase } from '@/lib/supabase';
import {
  consumePendingImages,
  findInlineImageIds,
  rewriteInlineImageSrcs,
} from '@/components/RichBodyEditor';

/** Walks `bodyHtml` for inline-image markers (the `data-pa-attach-id`
 *  attribute that `RichBodyEditor.handlePaste` stamps on every blob:
 *  `<img>` insertion), uploads each blob to permanent Storage, and
 *  returns the HTML rewritten to use public URLs.
 *
 *  No-op when the HTML has no inline-image markers (returns the input
 *  string verbatim). Throws if any single upload fails — the caller
 *  should catch and surface a toast to the user, NOT proceed with the
 *  save (otherwise the saved template would carry broken blob URLs).
 *
 *  Templates aren't ticket-scoped, so paths live under a `templates/`
 *  prefix in the existing `ticket-attachments` bucket. The bucket is
 *  already public-read, and authenticated team members can already
 *  insert there (same RLS as inline reply images), so this needs no
 *  schema migration. */
export async function uploadInlineTemplateImages(bodyHtml: string): Promise<string> {
  const ids = findInlineImageIds(bodyHtml);
  if (ids.length === 0) return bodyHtml;

  const fileMap = consumePendingImages(ids);
  if (fileMap.size === 0) {
    // The IDs exist in the HTML but the pending-images registry has no
    // matching File objects. Happens when the same template body has
    // already been processed (e.g. SaveAsTemplate fired, then user
    // saved a second time). In that case the original blob: srcs were
    // already rewritten to public URLs — the leftover marker IDs are
    // harmless. Return verbatim.
    return bodyHtml;
  }

  const idToUrl = new Map<string, string>();
  for (const [id, file] of fileMap.entries()) {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `templates/inline-${id}-${safeName}`;
    const { error } = await supabase.storage
      .from('ticket-attachments')
      .upload(path, file, {
        contentType: file.type || 'application/octet-stream',
        upsert: true,
      });
    if (error) {
      throw new Error(`Template image upload failed: ${error.message}`);
    }
    const { data } = supabase.storage.from('ticket-attachments').getPublicUrl(path);
    idToUrl.set(id, data.publicUrl);
  }

  return rewriteInlineImageSrcs(bodyHtml, idToUrl);
}
